import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { saveAs } from 'file-saver';

import CustomLoading from '../../../../util_components/custom_loading';
import DiscardModal from './discard_modal';
import HistoryTable from './history_table';
import PreviewTable from './preview_table';
import FlaggedRow from './flagged_row';
import {
  DECISION,
  apply_label,
  decision_progress,
  recorded_decisions,
  set_decision,
  set_reason
} from './decisions';
import DownloadCsvButton from '../../../../util_components/csv_export/download_csv_button';
import {
  HISTORY_CSV_COLUMNS,
  PREVIEW_CSV_COLUMNS,
  history_csv_loader,
  history_csv_name,
  preview_csv_name
} from './csv';
import { FilterBar, Segmented } from '../../shared/controls';
import {
  apply_upload,
  discard_upload,
  fetch_upload_file_link,
  fetch_upload_run,
  fetch_upload_runs,
  fetch_upload_template,
  post_recalc,
  validate_upload
} from '../../api';
import { notify_type } from '../adapters';
import {
  ROW_FILTER,
  ROW_FILTER_OPTIONS,
  apply_result_view,
  filter_rows,
  history_view,
  off_screen_apply_notice,
  plural,
  precheck_file,
  row_filter_summary,
  run_view
} from './adapters';
import { HISTORY_FAILED_TEXT, NEXT, upload_error_view } from './error_handlers';
import { FILE_CATEGORY_OPTION, RUN_STATUS, TEMPLATE_FILE_NAME, UPLOAD_LIMITS } from './params';
import { category_options } from '../form_logic';
import { apply_payload, discard_payload, recalc_payload } from './requests';
import { ist_month_key, with_ist } from '../../shared/ist';

const EMPTY_HISTORY = { rows: [], total_count: 0 };
const IDLE_RECALC = { running: false, done: 0, total: 0, failures: [] };

const UploadPanel = ({
  pay_month,
  month_text,
  can_write,
  why_not,
  now,
  on_error,
  on_notify,
  on_applied,
  // the manual entry form's list (GET /additional/categories): the file-level category choices.
  categories = [],
  // The tab's filter row: the closed panel's button sits there, beside "+ Add entry".
  toggle_host = null
}) => {
  const [uploading, setUploading] = useState(false);
  // '' = each row's own category column; a code = every row's category (the contract's `category`).
  const [file_category, setFileCategory] = useState('');
  const [template_busy, setTemplateBusy] = useState(false);
  // Errors about the file or the panel (validate, template, opening a run); run_error is about the open run.
  const [panel_error, setPanelError] = useState(null);
  const [run, setRun] = useState(null);
  const [run_error, setRunError] = useState(null);
  // PREVIEW_STALE and its kin: the preview no longer matches the data, so Apply is withdrawn.
  const [stale, setStale] = useState(false);
  const [ticks, setTicks] = useState({});
  // per-row KEEP/SKIP by line (decisions.js); collapsed compares by line (expanded by default)
  const [decisions, setDecisions] = useState({});
  const [collapsed, setCollapsed] = useState({});
  const [filter, setFilter] = useState(ROW_FILTER.ALL);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [recalc, setRecalc] = useState(IDLE_RECALC);
  const [discarding, setDiscarding] = useState(false);
  const [discard_busy, setDiscardBusy] = useState(false);
  const [discard_error, setDiscardError] = useState('');
  const [history_open, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(EMPTY_HISTORY);
  const [history_state, setHistoryState] = useState({ loading: true, error: '' });
  const [history_offset, setHistoryOffset] = useState(0);
  const [links, setLinks] = useState({});
  // Collapsed to one button until Ops open it, so the tab still leads with its entries list.
  const [open, setOpen] = useState(false);

  // An answer for a month no longer on screen is dropped (the tab's month picker moved on).
  const month_ref = useRef(pay_month);
  month_ref.current = pay_month;
  const history_seq = useRef(0);
  // Every request that ends by showing a run takes a ticket; only the newest ticket's answer is
  // shown.
  const run_seq = useRef(0);
  // The run on screen now, read by answers that arrive later.
  const run_ref = useRef(null);
  run_ref.current = run ? run.run_uuid : null;
  const file_ref = useRef(null);
  const toggle_ref = useRef(null);
  const heading_ref = useRef(null);
  const preview_ref = useRef(null);
  const result_ref = useRef(null);
  const focus_after = useRef(null);

  // Focus moves once the element it targets has rendered (the preview, the apply result). A list of
  // refs means the first one still on screen: the preview heading, else the panel heading.
  useEffect(() => {
    const wanted = focus_after.current;
    if (!wanted) return;
    const target = (Array.isArray(wanted) ? wanted : [wanted]).find((r) => r && r.current);
    if (target) {
      focus_after.current = null;
      target.current.focus();
    }
  });
  const focus_heading = () => {
    focus_after.current = [preview_ref, heading_ref];
  };

  const permission = (e, text) => on_error(e, text);
  const row_ctx = () => ({ current_month: ist_month_key(now()) });

  const load_history = (offset = 0) => {
    history_seq.current += 1;
    const mine = history_seq.current;
    const month = month_ref.current;
    setHistoryState({ loading: true, error: '' });
    fetch_upload_runs(month, UPLOAD_LIMITS.RUNS_LIMIT_DEFAULT, offset)
      .then((data) => {
        if (mine !== history_seq.current) return;
        setHistory(history_view(data));
        setHistoryOffset(offset);
        setHistoryState({ loading: false, error: '' });
      })
      .catch((e) => {
        if (mine !== history_seq.current) return;
        const v = upload_error_view(e);
        if (v.is_permission) permission(e, 'Failed to load the uploads.');
        setHistory(EMPTY_HISTORY);
        // The server's sentence alone, or ours when it sent none (never a "(0)" count).
        setHistoryState({ loading: false, error: v.server_message || HISTORY_FAILED_TEXT });
      });
  };

  // A month switch drops every answer still in flight (month_ref), so nothing may stay busy on them.
  useEffect(() => {
    setUploading(false);
    setApplying(false);
    setRun(null);
    setRunError(null);
    setPanelError(null);
    setResult(null);
    setRecalc(IDLE_RECALC);
    setDiscarding(false);
    setDiscardBusy(false);
    setLinks({});
    if (pay_month) load_history(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pay_month]);

  // keep_error: a re-read of the run on screen after a refusal; its message and the ticks stay.
  const show_run = (data, { keep_error = false } = {}) => {
    setRun(run_view(data, row_ctx()));
    setStale(false);
    if (keep_error) return;
    setTicks({});
    setDecisions({});
    setCollapsed({});
    setFilter(ROW_FILTER.ALL);
    setRunError(null);
  };

  const next_run_ticket = () => {
    run_seq.current += 1;
    return run_seq.current;
  };

  const open_run = (run_uuid, opts = {}) => {
    const month = month_ref.current;
    const ticket = next_run_ticket();
    setPanelError(null);
    fetch_upload_run(run_uuid)
      .then((data) => {
        if (month !== month_ref.current || ticket !== run_seq.current) return;
        show_run(data, opts);
        if (!opts.keep_error) focus_after.current = preview_ref;
        else if (opts.focus) focus_heading();
      })
      .catch((e) => {
        if (month !== month_ref.current || ticket !== run_seq.current) return;
        const v = upload_error_view(e);
        if (v.is_permission) {
          permission(e, 'Failed to load the upload.');
          return;
        }
        setPanelError(v);
      });
  };

  const download_template = () => {
    setTemplateBusy(true);
    setPanelError(null);
    fetch_upload_template()
      .then((csv) => {
        setTemplateBusy(false);
        saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), TEMPLATE_FILE_NAME);
      })
      .catch((e) => {
        setTemplateBusy(false);
        const v = upload_error_view(e);
        if (v.is_permission) permission(e, 'Failed to download the template.');
        else setPanelError(v);
      });
  };

  const choose_off = !can_write || uploading || applying || !pay_month;
  const choose_file = () => {
    if (choose_off || !file_ref.current) return;
    file_ref.current.click();
  };

  const on_file = (e) => {
    const file = e.target.files && e.target.files[0];
    // Cleared so choosing the same file again (after fixing it) still fires a change.
    e.target.value = '';
    if (!file || uploading) return;
    const refused = precheck_file(file);
    setResult(null);
    setRunError(null);
    if (refused) {
      setPanelError({ message: refused, next: NEXT.NONE, stale_rows: [], applied_run: null });
      return;
    }
    const month = month_ref.current;
    const ticket = next_run_ticket();
    setPanelError(null);
    setUploading(true);
    validate_upload(file, month, file_category)
      .then((data) => {
        if (month !== month_ref.current) return;
        setUploading(false);
        load_history(0);
        if (ticket !== run_seq.current) return;
        show_run(data);
        focus_after.current = preview_ref;
      })
      .catch((err) => {
        if (month !== month_ref.current) return;
        setUploading(false);
        const v = upload_error_view(err, 'upload');
        if (v.is_permission) {
          permission(err, 'The upload was refused.');
          return;
        }
        setPanelError(v);
        if (v.uncertain) load_history(0);
      });
  };

  // What a refusal about the open run does to it, beyond showing its message. Each of these
  // unmounts the button that was pressed, so focus goes to the preview heading, else the panel
  // heading.
  const follow_run_error = (v, run_uuid) => {
    if (v.next === NEXT.REUPLOAD) {
      setStale(true);
      focus_heading();
    }
    if (v.next === NEXT.RELOAD_RUN) open_run(run_uuid, { keep_error: true, focus: true });
    if (v.next === NEXT.CLOSE_RUN) {
      setRun(null);
      setRunError(null);
      setPanelError(v);
      focus_after.current = heading_ref;
      load_history(0);
      return;
    }
    setRunError(v);
  };

  const raw_rows = (run && run.raw && run.raw.rows) || [];
  const progress = decision_progress(raw_rows, decisions);
  // the flagged rows are acknowledged by deciding each; only the invalid-rows tick remains
  const shown_ticks = run ? run.ticks.filter((t) => t.key === 'skip_invalid') : [];
  const ready = !!run && shown_ticks.every((t) => !!ticks[t.key]) && progress.complete;
  const apply_off = applying || !ready;

  const apply = () => {
    if (apply_off) return;
    const month = month_ref.current;
    const { run_uuid } = run;
    setApplying(true);
    setRunError(null);
    setResult(null);
    apply_upload(apply_payload(run.raw, ticks, decisions))
      .then((data) => {
        if (month !== month_ref.current) {
          const n = off_screen_apply_notice(month, data);
          on_notify(n.type, n.message);
          on_applied();
          return;
        }
        setApplying(false);
        const res = apply_result_view(data);
        on_notify(notify_type(res.tone), res.message);
        load_history(0);
        // The month's payable moved: the tab reloads its entries, the shell its badges.
        on_applied();
        // Another run opened meanwhile: the notice says what happened, the newer preview stays.
        if (run_ref.current !== run_uuid) return;
        show_run(data.run || {});
        setResult(res);
        setRecalc(IDLE_RECALC);
        focus_after.current = result_ref;
      })
      .catch((e) => {
        const v = upload_error_view(e, 'apply');
        if (month !== month_ref.current) {
          // Ops moved on mid-apply: never drop a money outcome silently.
          if (v.is_permission) {
            permission(e, 'The apply was refused.');
            return;
          }
          console.error('CSV apply failed after a month switch', month, v.status || 'no answer');
          const n = off_screen_apply_notice(month, null, v);
          on_notify(n.type, n.message);
          on_applied();
          return;
        }
        setApplying(false);
        if (v.is_permission) {
          permission(e, 'The apply was refused.');
          return;
        }
        // A 5xx may have committed, or recorded why it failed: the list must show what is saved.
        if (v.uncertain || (v.status && v.status >= 500)) {
          load_history(0);
          on_applied();
        }
        if (run_ref.current !== run_uuid) return;
        follow_run_error(v, run_uuid);
        // re-read the run so its last_apply_error (or its APPLIED state) shows.
        if (v.status >= 500 && v.next === NEXT.NONE) open_run(run_uuid, { keep_error: true });
      });
  };

  const open_discard = () => {
    setDiscardError('');
    setDiscarding(true);
  };
  const close_preview = () => {
    setRun(null);
    setRunError(null);
    focus_after.current = heading_ref;
  };

  const close_discard = () => {
    setDiscarding(false);
    setDiscardBusy(false);
    setDiscardError('');
  };

  const submit_discard = (reason) => {
    const month = month_ref.current;
    const { run_uuid, file_name } = run;
    setDiscardBusy(true);
    setDiscardError('');
    discard_upload(discard_payload(run_uuid, reason))
      .then(() => {
        if (month !== month_ref.current) return;
        close_discard();
        setRun(null);
        setRunError(null);
        on_notify('success', `Discarded ${file_name}. Nothing from it was paid.`);
        load_history(0);
      })
      .catch((e) => {
        if (month !== month_ref.current) return;
        setDiscardBusy(false);
        const v = upload_error_view(e);
        if (v.is_permission) {
          close_discard();
          permission(e, 'The discard was refused.');
          return;
        }
        // The run changed or went away: the dialog has nothing left to discard.
        if (v.next === NEXT.RELOAD_RUN || v.next === NEXT.CLOSE_RUN) {
          close_discard();
          follow_run_error(v, run_uuid);
          return;
        }
        setDiscardError(v.message);
      });
  };

  // one teacher per POST /recalc, in order, with a running count; failures stay listed.
  const recalc_remaining = () => {
    if (recalc.running || !result || !result.remaining.length) return;
    const month = month_ref.current;
    const list = result.remaining;
    const names = (run && run.teacher_names) || {};
    const failures = [];
    setRecalc({ running: true, done: 0, total: list.length, failures: [] });
    const finish = () => {
      setRecalc({ running: false, done: list.length, total: list.length, failures });
      setResult((r) => (r ? { ...r, remaining: failures.map((f) => f.teacher_uuid) } : r));
      on_applied();
    };
    const step = (i) => {
      if (month !== month_ref.current) return;
      if (i >= list.length) {
        finish();
        return;
      }
      const teacher_uuid = list[i];
      const name = names[teacher_uuid] || teacher_uuid;
      post_recalc(recalc_payload(teacher_uuid, month))
        .then((data) => {
          const r = (data && data.recompute) || {};
          if (r.ok === false) {
            failures.push({
              teacher_uuid,
              text: `${name}: ${r.error || 'the recalculation failed.'}`
            });
          }
          setRecalc((s) => ({ ...s, done: i + 1 }));
          step(i + 1);
        })
        .catch((e) => {
          const v = upload_error_view(e);
          if (v.is_permission) {
            setRecalc(IDLE_RECALC);
            permission(e, 'The recalculation was refused.');
            return;
          }
          failures.push({ teacher_uuid, text: `${name}: ${v.message}` });
          setRecalc((s) => ({ ...s, done: i + 1 }));
          step(i + 1);
        });
    };
    step(0);
  };

  const get_link = (run_uuid) => {
    const month = month_ref.current;
    setLinks((l) => ({ ...l, [run_uuid]: { busy: true } }));
    fetch_upload_file_link(run_uuid)
      .then((data) => {
        if (month !== month_ref.current) return;
        setLinks((l) => ({
          ...l,
          [run_uuid]: {
            url: data.url,
            expires_text: data.expires_at_ist
              ? `Link valid until ${with_ist(data.expires_at_ist)}`
              : ''
          }
        }));
      })
      .catch((e) => {
        if (month !== month_ref.current) return;
        const v = upload_error_view(e);
        if (v.is_permission) permission(e, 'Failed to get the file link.');
        setLinks((l) => ({ ...l, [run_uuid]: { error: v.message } }));
      });
  };

  const on_focus_lost = () => {
    if (heading_ref.current) heading_ref.current.focus();
  };

  const open_panel = () => {
    focus_after.current = heading_ref;
    setOpen(true);
  };
  const close_panel = () => {
    focus_after.current = toggle_ref;
    setOpen(false);
  };

  const rows = run ? filter_rows(run.rows, filter) : [];
  const raw_by_line = {};
  raw_rows.forEach((r) => {
    raw_by_line[r.line] = r;
  });
  // "Compare and decide" on All rows: open that row's card on the Needs confirmation tab
  const compare_line = (line) => {
    setCollapsed((c) => ({ ...c, [line]: false }));
    setFilter(ROW_FILTER.CONFIRM);
    focus_after.current = {
      get current() {
        return document.getElementById(`tp-flag-${line}-title`);
      }
    };
  };
  const file_category_options = [FILE_CATEGORY_OPTION, ...category_options(categories)];
  const category_refused = !!(panel_error && panel_error.category_refused);
  const row_summary = run ? row_filter_summary(run.rows.length, rows.length, filter) : null;
  const can_discard = !!run && run.status === RUN_STATUS.VALIDATED && run.can_act;
  const show_apply = !!run && run.can_apply && run.can_act && !stale;
  const recorded = recorded_decisions(raw_rows);
  const show_not_yours = !!run && run.status === RUN_STATUS.VALIDATED && !run.can_act;
  const history_prev_off = history_offset <= 0;
  let history_toggle_suffix = ` (${history.total_count})`;
  if (history_state.loading) history_toggle_suffix = '';
  else if (history_state.error) history_toggle_suffix = ' (could not load)';
  const history_next_off = history_offset + UPLOAD_LIMITS.RUNS_LIMIT_DEFAULT >= history.total_count;

  const error_banner = (v, testid) =>
    v ? (
      <div className="tp-banner tp-banner-error" role="alert" data-testid={testid}>
        <div id={`${testid}-text`}>{v.message}</div>
        {v.stale_rows.length ? (
          <ul className="tp-dup-list">
            {v.stale_rows.map((r) => (
              <li key={r.line}>{r.message}</li>
            ))}
          </ul>
        ) : null}
        {v.applied_run ? (
          <button
            type="button"
            className="tp-link"
            onClick={() => open_run(v.applied_run.run_uuid)}
          >
            View that upload
          </button>
        ) : null}
      </div>
    ) : null;

  // The header toggle stays while the panel is open, in its pressed state (it closes it again).
  const toggle = (
    <button
      type="button"
      className={`tp-btn-out${open ? ' tp-btn-pressed' : ''}`}
      ref={toggle_ref}
      aria-expanded={open ? 'true' : 'false'}
      onClick={open ? close_panel : open_panel}
    >
      Upload a CSV
    </button>
  );
  if (!open) {
    if (toggle_host) return createPortal(toggle, toggle_host);
    return (
      <div className="tp-frow">
        <div className="tp-frow-right">{toggle}</div>
      </div>
    );
  }

  return (
    <div className="tp-card tp-upload" data-testid="tp-upload-panel">
      {toggle_host ? createPortal(toggle, toggle_host) : null}
      <div className="tp-card-hd">
        <span className="tp-card-hd-title" ref={heading_ref} tabIndex={-1}>
          Upload a CSV
        </span>
        <button
          type="button"
          className="tp-dialog-x tp-upload-x"
          aria-label="Close upload"
          onClick={close_panel}
        >
          <svg viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
            <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z" />
          </svg>
        </button>
        <input
          ref={file_ref}
          type="file"
          accept=".csv,text/csv"
          aria-label="CSV file to upload"
          data-testid="tp-upload-input"
          style={{ display: 'none' }}
          onChange={on_file}
        />
      </div>
      <div className="tp-form">
        <div className="tp-upload-grid">
          <div className="tp-upz">
            <span>The CSV for {month_text}</span>
            <div className="tp-field tp-upload-cat">
              <label htmlFor="tp-upload-category">Category for this file</label>
              <select
                id="tp-upload-category"
                className="tp-select"
                value={file_category}
                disabled={uploading}
                aria-invalid={category_refused ? 'true' : undefined}
                aria-describedby={category_refused ? 'tp-upload-error-text' : undefined}
                onChange={(e) => {
                  setFileCategory(e.target.value);
                  if (category_refused) setPanelError(null);
                }}
              >
                {file_category_options.map((o) => (
                  <option key={o.value || 'file'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {/* aria-disabled, not disabled: each button keeps focus while its request runs. */}
            <button
              type="button"
              className="tp-btn-out"
              onClick={choose_off ? undefined : choose_file}
              aria-disabled={choose_off ? 'true' : undefined}
              title={can_write ? '' : why_not}
            >
              {uploading ? 'Checking the file…' : 'Choose CSV file'}
            </button>
          </div>
          <div className="tp-upload-rules">
            <ul>
              <li>One file per payment month ({month_text}).</li>
              <li>Up to {plural(UPLOAD_LIMITS.MAX_ROWS, 'row')} and 2 MB.</li>
              <li>
                category: the name shown in the entry form (or its code). Leave it blank when a
                category is chosen above.
              </li>
              <li>
                reference_id is optional: leave it blank and one is generated from the row. The file
                is checked and shown here before anything is paid.
              </li>
            </ul>
            <div className="tp-upload-links">
              <button
                type="button"
                className="tp-link"
                onClick={template_busy ? undefined : download_template}
                aria-disabled={template_busy ? 'true' : undefined}
              >
                Download template
              </button>
              <button
                type="button"
                className="tp-link"
                aria-expanded={history_open ? 'true' : 'false'}
                onClick={() => setHistoryOpen((o) => !o)}
              >
                {history_open ? 'Hide' : 'Show'} earlier uploads for {month_text}
                {history_toggle_suffix}
              </button>
            </div>
          </div>
        </div>
        {uploading ? <CustomLoading /> : null}
        {error_banner(panel_error, 'tp-upload-error')}
        {result ? (
          <div
            className={`tp-banner ${result.tone === 'success' ? 'tp-banner-ok' : 'tp-banner-warn'}`}
            role="status"
            ref={result_ref}
            tabIndex={-1}
            data-testid="tp-upload-result"
          >
            <div>{result.message}</div>
            {result.warnings.map((w, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={`${i}-${w}`}>{w}</div>
            ))}
            {recalc.running ? (
              <div>
                Recalculated {recalc.done} of {recalc.total}…
              </div>
            ) : null}
            {recalc.failures.length ? (
              <ul className="tp-dup-list">
                {recalc.failures.map((f) => (
                  <li key={f.teacher_uuid}>{f.text}</li>
                ))}
              </ul>
            ) : null}
            {result.remaining.length ? (
              <button
                type="button"
                className="tp-btn-out"
                onClick={recalc.running ? undefined : recalc_remaining}
                aria-disabled={recalc.running ? 'true' : undefined}
              >
                Recalculate the remaining {result.remaining.length}
              </button>
            ) : null}
          </div>
        ) : null}

        {run ? (
          <div data-testid="tp-upload-preview">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title" ref={preview_ref} tabIndex={-1}>
                {run.file_name} · {run.month_text}
              </span>
              <span className="tp-card-hd-sub">
                {run.uploaded_text}
                {run.upload_category_label ? ` · Category: ${run.upload_category_label}` : ''}
              </span>
              <span className="tp-card-hd-acts">
                <DownloadCsvButton
                  columns={PREVIEW_CSV_COLUMNS}
                  rows={rows}
                  file_name={() => preview_csv_name(pay_month, run.file_name)}
                />
              </span>
            </div>
            <div className="tp-pvg">
              <div className="tp-pv">
                <div className="tp-pv-n">{run.counts.total}</div>
                <div className="tp-pv-l">Total rows</div>
              </div>
              <div className="tp-pv">
                <div className="tp-pv-n tp-pos">{run.counts.valid}</div>
                <div className="tp-pv-l">Valid rows</div>
              </div>
              <div className="tp-pv">
                <div className="tp-pv-n tp-neg">{run.counts.invalid}</div>
                <div className="tp-pv-l">Invalid rows</div>
              </div>
            </div>
            {run.generated_text ? (
              <div className="tp-banner tp-banner-info" data-testid="tp-upload-generated">
                {run.generated_text}
              </div>
            ) : null}
            {run.applied_text ? (
              <div className="tp-banner tp-banner-ok">
                <div>{run.applied_text}</div>
                {run.skipped_texts.length ? (
                  <ul className="tp-dup-list" data-testid="tp-upload-skipped">
                    {run.skipped_texts.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
            {run.discarded_text ? (
              <div className="tp-banner tp-banner-info">{run.discarded_text}</div>
            ) : null}
            {run.last_apply_error && run.status === RUN_STATUS.VALIDATED ? (
              <div className="tp-banner tp-banner-warn">
                The last apply failed: {run.last_apply_error}
              </div>
            ) : null}
            {!run.can_apply && run.blocked_reason ? (
              <div className="tp-banner tp-banner-info" data-testid="tp-upload-blocked">
                {run.blocked_reason}
              </div>
            ) : null}
            {show_not_yours ? (
              <div className="tp-banner tp-banner-info" data-testid="tp-upload-not-yours">
                {run.not_yours_text}
              </div>
            ) : null}
            {error_banner(run_error, 'tp-upload-run-error')}

            {show_apply ? (
              <div data-testid="tp-upload-apply">
                {progress.total ? (
                  <div
                    className={`tp-flag-progress${progress.complete ? ' tp-pos' : ''}`}
                    role="status"
                    data-testid="tp-upload-progress"
                  >
                    {progress.text}
                    {progress.complete ? '' : ': decide each one under Needs confirmation.'}
                  </div>
                ) : null}
                {shown_ticks.map((t) => (
                  <label key={t.key} className="tp-field">
                    <span>
                      <input
                        type="checkbox"
                        checked={!!ticks[t.key]}
                        disabled={applying}
                        onChange={(e) => setTicks((x) => ({ ...x, [t.key]: e.target.checked }))}
                      />{' '}
                      {t.label}
                    </span>
                  </label>
                ))}
                {!ready ? (
                  <div className="tp-hint" id="tp-upload-apply-hint">
                    {progress.complete
                      ? 'Tick the box above to apply.'
                      : 'Decide every flagged row to apply.'}
                  </div>
                ) : null}
                <div className="tp-modal-ft">
                  <button
                    type="button"
                    className="tp-btn"
                    onClick={apply_off ? undefined : apply}
                    aria-disabled={apply_off ? 'true' : undefined}
                    aria-describedby={ready ? undefined : 'tp-upload-apply-hint'}
                  >
                    {applying ? 'Applying…' : apply_label(raw_rows, decisions)}
                  </button>
                </div>
              </div>
            ) : null}

            <FilterBar
              label="Preview rows"
              summary={
                <>
                  Showing <b>{row_summary.lead}</b>
                  {row_summary.rest}
                </>
              }
            >
              <Segmented
                label="Show rows"
                options={ROW_FILTER_OPTIONS}
                value={filter}
                onChange={setFilter}
              />
              <span className="tp-fbar-end">
                {can_discard ? (
                  // aria-disabled while the apply runs: the run may be APPLIED by the time it answers.
                  <button
                    type="button"
                    className="tp-btn-ghost"
                    onClick={applying ? undefined : open_discard}
                    aria-disabled={applying ? 'true' : undefined}
                  >
                    Discard
                  </button>
                ) : (
                  <button type="button" className="tp-btn-ghost" onClick={close_preview}>
                    Close preview
                  </button>
                )}
              </span>
            </FilterBar>
            {filter === ROW_FILTER.CONFIRM && rows.length ? (
              <div className="tp-flags">
                {rows.map((r) => (
                  <FlaggedRow
                    key={r.key}
                    view={r}
                    raw={raw_by_line[r.line]}
                    rows={raw_rows}
                    decision={decisions[r.line]}
                    expanded={!collapsed[r.line]}
                    can_decide={show_apply && !applying}
                    on_toggle={() => setCollapsed((c) => ({ ...c, [r.line]: !c[r.line] }))}
                    on_decide={(line, action) => setDecisions((d) => set_decision(d, line, action))}
                    on_reason={(line, reason) => setDecisions((d) => set_reason(d, line, reason))}
                  />
                ))}
              </div>
            ) : (
              <PreviewTable
                rows={rows}
                decisions={show_apply ? decisions : Object.keys(recorded).length ? recorded : null}
                can_decide={show_apply && !applying}
                on_skip={(line) => setDecisions((d) => set_decision(d, line, DECISION.SKIP))}
                on_compare={compare_line}
              />
            )}
          </div>
        ) : null}

        {history_open ? (
          <div data-testid="tp-upload-history">
            <div className="tp-card-hd">
              <span className="tp-card-hd-title">Earlier uploads · {month_text}</span>
              <span className="tp-card-hd-acts">
                <DownloadCsvButton
                  columns={HISTORY_CSV_COLUMNS}
                  load_rows={history_csv_loader(fetch_upload_runs, pay_month)}
                  file_name={() => history_csv_name(pay_month)}
                  disabled={history_state.loading || !!history_state.error}
                  on_error={(e) => {
                    if (upload_error_view(e).is_permission)
                      permission(e, 'Failed to load the uploads.');
                  }}
                />
              </span>
            </div>
            {history_state.loading ? <CustomLoading /> : null}
            {!history_state.loading && history_state.error ? (
              <div className="tp-banner tp-banner-error" role="alert">
                <span>{history_state.error}</span>{' '}
                <button
                  type="button"
                  className="tp-link"
                  onClick={() => load_history(history_offset)}
                >
                  Try again
                </button>
              </div>
            ) : null}
            {!history_state.loading && !history_state.error ? (
              <HistoryTable
                rows={history.rows}
                links={links}
                on_open={(uuid) => open_run(uuid)}
                on_link={get_link}
              />
            ) : null}
            {history.total_count > UPLOAD_LIMITS.RUNS_LIMIT_DEFAULT ? (
              <div className="tp-frow">
                {/* aria-disabled, not disabled: Next on the last page keeps focus. */}
                <button
                  type="button"
                  className="tp-btn-ghost"
                  onClick={
                    history_prev_off
                      ? undefined
                      : () =>
                          load_history(
                            Math.max(0, history_offset - UPLOAD_LIMITS.RUNS_LIMIT_DEFAULT)
                          )
                  }
                  aria-disabled={history_prev_off ? 'true' : undefined}
                >
                  Prev
                </button>
                <button
                  type="button"
                  className="tp-btn-ghost"
                  onClick={
                    history_next_off
                      ? undefined
                      : () => load_history(history_offset + UPLOAD_LIMITS.RUNS_LIMIT_DEFAULT)
                  }
                  aria-disabled={history_next_off ? 'true' : undefined}
                >
                  Next
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {discarding && run ? (
        <DiscardModal
          run={run}
          saving={discard_busy}
          error_text={discard_error}
          on_submit={submit_discard}
          on_close={close_discard}
          on_focus_lost={on_focus_lost}
        />
      ) : null}
    </div>
  );
};

export default UploadPanel;
